import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ResultRow } from '../api';
import { Card, Empty, StatusBadge, relativeTime } from '../components/ui';

export function ResultsTab({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<ResultRow[] | null>(null);

  const load = () =>
    api.get<ResultRow[]>(`/api/tenants/${tenantId}/results`).then(setRows).catch(() => setRows([]));
  useEffect(() => { void load(); }, [tenantId]);

  if (rows === null) return <p className="faint">Loading…</p>;

  return (
    <Card
      title="Recent test results"
      hint="Every SSO attempt against this tenant, newest first. Results expire on their own — this is a debugging log, not an audit trail."
      actions={
        <div className="row">
          <button className="tiny" onClick={load}>Refresh</button>
          {rows.length > 0 && (
            <button
              className="tiny danger"
              onClick={async () => {
                if (!confirm('Delete every stored result for this tenant?')) return;
                await api.del(`/api/tenants/${tenantId}/results`);
                await load();
              }}
            >Clear all</button>
          )}
        </div>
      }
    >
      {rows.length === 0 ? (
        <Empty>
          Nothing captured yet. Launch a login from the SAML or OIDC tab, or send a request
          through PingAccess to the protected app URL.
        </Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>When</th><th>Protocol</th><th>Connection</th><th>Subject</th><th>Checks</th><th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="dim nowrap">{relativeTime(r.createdAt)}</td>
                <td><span className="badge proto">{r.protocol}</span></td>
                <td>{r.connectionName}</td>
                <td className="mono">{r.subject || <span className="faint">—</span>}</td>
                <td className="nowrap mono small">
                  <span style={{ color: 'var(--pass)' }}>{r.checkCounts.pass}✓</span>
                  {r.checkCounts.warn > 0 && <span style={{ color: 'var(--warn)' }}> {r.checkCounts.warn}!</span>}
                  {r.checkCounts.fail > 0 && <span style={{ color: 'var(--fail)' }}> {r.checkCounts.fail}✕</span>}
                </td>
                <td><StatusBadge status={r.status} /></td>
                <td><Link to={`/result/${r.id}`}>Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
